import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Plus,
  Trash2,
  Edit3,
  Save,
  X,
  UserCheck,
  Phone,
  Lock,
  Loader2,
  Copy,
  Upload,
  Camera,
  CreditCard,
} from "lucide-react";
import { confirmDialog } from "../../utils/confirmDialog";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reusable mini image uploader for rep photos (face / id front / id back)
const RepImageSlot = ({ label, icon: Icon, value, onChange, testId, accent = "#3B82F6" }) => {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("صورة فقط"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("أقل من 5 ميغا"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/upload-image`, fd);
      onChange(data.path);
      toast.success(`تم رفع ${label}`);
    } catch {
      toast.error("فشل الرفع");
    } finally { setUploading(false); }
  };

  return (
    <label
      data-testid={testId}
      className="flex flex-col items-center gap-1 cursor-pointer group"
      style={{ minWidth: 0 }}
    >
      <div
        className="relative w-20 h-20 rounded-2xl border-2 border-dashed overflow-hidden flex items-center justify-center bg-white"
        style={{ borderColor: value ? accent : "#BAE6FD" }}
      >
        {value ? (
          <img src={`${API}/files/${value}`} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className="text-center px-1">
            <Icon className="w-5 h-5 mx-auto mb-0.5" style={{ color: accent }} />
            <p className="text-[9px] text-[#5C6E64] leading-tight">{label}</p>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-white" />
          </div>
        )}
        {value && !uploading && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); onChange(null); }}
            className="absolute top-0.5 left-0.5 w-5 h-5 bg-[#E85D45] text-white rounded-full flex items-center justify-center shadow"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      {value && <span className="text-[9px] text-[#1c7c3a] font-bold">✓ مرفوعة</span>}
      <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
    </label>
  );
};

const RepsTab = () => {
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    full_name: "", phone: "", password: "",
    photo: null, id_front: null, id_back: null,
  });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchReps = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/reps`);
      setReps(data || []);
    } catch {
      toast.error("فشل تحميل المشرفين");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchReps(); }, []);

  const handleCreate = async () => {
    if (!form.full_name.trim() || !form.phone.trim() || form.password.length < 4) {
      toast.error("الاسم والهاتف وكلمة سر (4+ أحرف) مطلوبة");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/reps`, form);
      toast.success("تم إنشاء حساب المشرف");
      setForm({ full_name: "", phone: "", password: "", photo: null, id_front: null, id_back: null });
      fetchReps();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الإنشاء");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (rep) => {
    if (!(await confirmDialog(`حذف المشرف "${rep.full_name}"؟`))) return;
    try {
      await axios.delete(`${API}/admin/reps/${rep.id}`);
      toast.success("تم الحذف");
      fetchReps();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الحذف");
    }
  };

  const startEdit = (rep) => {
    setEditingId(rep.id);
    setEditForm({
      full_name: rep.full_name || "",
      phone: rep.phone || "",
      password: "",
      photo: rep.photo || null,
      id_front: rep.id_front || null,
      id_back: rep.id_back || null,
    });
  };

  const handleSaveEdit = async (rep) => {
    try {
      const payload = { ...editForm };
      // strip empty password so backend doesn't try to hash empty
      if (!payload.password) delete payload.password;
      await axios.patch(`${API}/admin/reps/${rep.id}`, payload);
      toast.success("تم التحديث");
      setEditingId(null);
      setEditForm({});
      fetchReps();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل التحديث");
    }
  };

  const copyLoginInfo = (rep) => {
    const link = window.location.origin + "/login";
    const msg = `🔵 جُملتي — حساب مشرف\n\nاسم المستخدم (الهاتف): ${rep.phone}\nرابط الدخول: ${link}\n\nبعد الدخول ستصلك قائمة الزبائن.`;
    navigator.clipboard.writeText(msg);
    toast.success("تم نسخ بيانات الدخول — أرسلها للمشرف عبر واتساب");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="reps-tab">
      {/* Create form */}
      <div className="bg-white rounded-2xl p-4 border-2 border-[#1E3A5F]/20 shadow-md">
        <h3 className="font-bold text-[#1E3A5F] mb-3 flex items-center gap-2">
          <Plus className="w-5 h-5" />إضافة مشرف جديد
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
          <div className="relative">
            <UserCheck className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
            <input
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              data-testid="rep-create-name"
              placeholder="اسم المشرف"
              className="w-full pr-9 pl-3 py-2 rounded-full border border-[#E4EBE6] text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
            />
          </div>
          <div className="relative">
            <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
            <input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="rep-create-phone"
              placeholder="07XX XXX XXXX"
              className="w-full pr-9 pl-3 py-2 rounded-full border border-[#E4EBE6] text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
              dir="ltr"
            />
          </div>
          <div className="relative">
            <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
            <input
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="rep-create-password"
              placeholder="كلمة السر (4+ أحرف)"
              className="w-full pr-9 pl-3 py-2 rounded-full border border-[#E4EBE6] text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
            />
          </div>
        </div>

        {/* Photo slots — face + id front + id back */}
        <div className="bg-[#F8FAFC] border border-[#E4EBE6] rounded-xl p-3 mb-3">
          <p className="text-xs font-bold text-[#1E3A5F] mb-2 flex items-center gap-1">
            <Upload className="w-3.5 h-3.5" />
            صور المشرف (اختياري — يمكن رفعها لاحقاً)
          </p>
          <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
            <RepImageSlot
              label="صورة الوجه"
              icon={Camera}
              value={form.photo}
              onChange={(v) => setForm({ ...form, photo: v })}
              testId="rep-create-photo"
              accent="#3B82F6"
            />
            <RepImageSlot
              label="هوية أمامية"
              icon={CreditCard}
              value={form.id_front}
              onChange={(v) => setForm({ ...form, id_front: v })}
              testId="rep-create-id-front"
              accent="#F5A623"
            />
            <RepImageSlot
              label="هوية خلفية"
              icon={CreditCard}
              value={form.id_back}
              onChange={(v) => setForm({ ...form, id_back: v })}
              testId="rep-create-id-back"
              accent="#7ED321"
            />
          </div>
        </div>

        <button
          onClick={handleCreate}
          disabled={creating}
          data-testid="rep-create-submit"
          className="w-full bg-[#7ED321] hover:bg-[#6cb91d] disabled:opacity-50 text-white font-bold py-2 rounded-full flex items-center justify-center gap-2"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          إنشاء حساب المشرف
        </button>
      </div>

      {/* Reps list */}
      {reps.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-2xl border-2 border-dashed border-[#BAE6FD]">
          <UserCheck className="w-12 h-12 mx-auto text-[#BAE6FD] mb-2" />
          <p className="text-[#5C6E64]">لا يوجد مشرفون بعد</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {reps.map((r) => {
            const isEditing = editingId === r.id;
            return (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={`rep-card-${r.id}`}
                className="bg-white rounded-2xl p-4 border border-[#E4EBE6] shadow-sm"
              >
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {r.photo ? (
                      <img
                        src={`${API}/files/${r.photo}`}
                        alt={r.full_name}
                        className="w-12 h-12 rounded-full object-cover border-2 border-[#3B82F6] shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#3B82F6] text-white flex items-center justify-center shrink-0">
                        <UserCheck className="w-5 h-5" />
                      </div>
                    )}
                    {isEditing ? (
                      <input
                        value={editForm.full_name ?? r.full_name}
                        onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                        data-testid={`rep-edit-name-${r.id}`}
                        className="font-bold border border-[#E4EBE6] rounded px-2 py-1 text-sm flex-1 min-w-0"
                      />
                    ) : (
                      <p className="font-bold text-[#111814] truncate">{r.full_name}</p>
                    )}
                  </div>
                </div>

                {isEditing ? (
                  <div className="space-y-2">
                    <input
                      value={editForm.phone ?? r.phone}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                      data-testid={`rep-edit-phone-${r.id}`}
                      placeholder="الهاتف"
                      dir="ltr"
                      className="w-full px-3 py-1.5 rounded-lg border border-[#E4EBE6] text-sm"
                    />
                    <input
                      type="text"
                      value={editForm.password || ""}
                      onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                      data-testid={`rep-edit-password-${r.id}`}
                      placeholder="كلمة سر جديدة (اتركها فارغة لتبقى كما هي)"
                      className="w-full px-3 py-1.5 rounded-lg border border-[#E4EBE6] text-sm"
                    />

                    <div className="bg-[#F8FAFC] border border-[#E4EBE6] rounded-xl p-2">
                      <p className="text-[10px] font-bold text-[#1E3A5F] mb-2">صور المشرف:</p>
                      <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                        <RepImageSlot
                          label="صورة الوجه"
                          icon={Camera}
                          value={editForm.photo}
                          onChange={(v) => setEditForm({ ...editForm, photo: v })}
                          testId={`rep-edit-photo-${r.id}`}
                          accent="#3B82F6"
                        />
                        <RepImageSlot
                          label="هوية أمامية"
                          icon={CreditCard}
                          value={editForm.id_front}
                          onChange={(v) => setEditForm({ ...editForm, id_front: v })}
                          testId={`rep-edit-id-front-${r.id}`}
                          accent="#F5A623"
                        />
                        <RepImageSlot
                          label="هوية خلفية"
                          icon={CreditCard}
                          value={editForm.id_back}
                          onChange={(v) => setEditForm({ ...editForm, id_back: v })}
                          testId={`rep-edit-id-back-${r.id}`}
                          accent="#7ED321"
                        />
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSaveEdit(r)}
                        data-testid={`rep-save-${r.id}`}
                        className="flex-1 bg-[#7ED321] text-white py-1.5 rounded-full text-sm font-bold flex items-center justify-center gap-1"
                      >
                        <Save className="w-3.5 h-3.5" />حفظ
                      </button>
                      <button
                        onClick={() => { setEditingId(null); setEditForm({}); }}
                        className="px-4 bg-[#F3F6F4] text-[#5C6E64] py-1.5 rounded-full text-sm"
                      >
                        إلغاء
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-1 text-sm mb-3">
                      <div className="flex justify-between bg-[#F3F6F4] rounded-lg px-3 py-1.5">
                        <span className="text-[#5C6E64]">الهاتف:</span>
                        <span className="font-bold text-[#1E3A5F]" dir="ltr">{r.phone}</span>
                      </div>
                      <div className="flex justify-between bg-[#F3F6F4] rounded-lg px-3 py-1.5">
                        <span className="text-[#5C6E64]">أُنشئ في:</span>
                        <span className="text-xs text-[#111814]" dir="ltr">
                          {(r.created_at || "").slice(0, 10)}
                        </span>
                      </div>
                    </div>

                    {/* Display ID thumbnails when present */}
                    {(r.id_front || r.id_back) && (
                      <div className="flex gap-2 mb-3 justify-center">
                        {r.id_front && (
                          <a href={`${API}/files/${r.id_front}`} target="_blank" rel="noopener noreferrer" className="block">
                            <img
                              src={`${API}/files/${r.id_front}`}
                              alt="هوية أمامية"
                              className="w-14 h-14 rounded-lg object-cover border-2 border-[#F5A623]"
                              data-testid={`rep-thumb-id-front-${r.id}`}
                            />
                          </a>
                        )}
                        {r.id_back && (
                          <a href={`${API}/files/${r.id_back}`} target="_blank" rel="noopener noreferrer" className="block">
                            <img
                              src={`${API}/files/${r.id_back}`}
                              alt="هوية خلفية"
                              className="w-14 h-14 rounded-lg object-cover border-2 border-[#7ED321]"
                              data-testid={`rep-thumb-id-back-${r.id}`}
                            />
                          </a>
                        )}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <button
                        onClick={() => copyLoginInfo(r)}
                        data-testid={`rep-copy-${r.id}`}
                        className="flex-1 bg-[#3B82F6] hover:bg-[#2563eb] text-white py-1.5 rounded-full text-xs font-bold flex items-center justify-center gap-1"
                      >
                        <Copy className="w-3 h-3" />نسخ بيانات الدخول
                      </button>
                      <button
                        onClick={() => startEdit(r)}
                        data-testid={`rep-edit-${r.id}`}
                        className="bg-[#F5A623] hover:bg-[#d18d10] text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-1"
                      >
                        <Edit3 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleDelete(r)}
                        data-testid={`rep-delete-${r.id}`}
                        className="bg-[#E85D45] hover:bg-[#c14a36] text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RepsTab;
